import React, { useState } from "react";
import axios from "axios";
import { Send, Loader2, Briefcase, Users, HeartHandshake, TrendingUp, Paperclip, X } from "lucide-react";
import { company } from "../mock";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const YES_NO = ["Yes", "No"];
const EXPERIENCE = ["No experience", "Less than 1 year", "1-3 years", "3-5 years", "5+ years"];
const EMPLOYMENT = ["Full-time", "Part-time", "Flexible / Ad-hoc"];
const AVAILABILITY = ["Weekday mornings", "Weekday afternoons", "Evenings", "Early mornings", "Weekends"];

const initialForm = {
  firstName: "", lastName: "", email: "", phone: "", location: "",
  experienceYears: "", experience: "",
  drivingLicence: "", ownTransport: "", rightToWork: "",
  employmentType: "", availability: [], startDate: "", notes: "",
};

const perks = [
  { icon: Users, title: "Supportive team", text: "Join a friendly, close-knit team that looks out for each other." },
  { icon: TrendingUp, title: "Room to grow", text: "Opportunities to progress into supervisor and management roles." },
  { icon: HeartHandshake, title: "Fair & flexible", text: "Flexible hours built around your availability and life." },
];

const Careers = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [cv, setCv] = useState(null); // { name, base64, mime }

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const handleChange = (e) => setField(e.target.name, e.target.value);

  const handleCv = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const allowed = [".pdf", ".doc", ".docx"];
    const okType = allowed.some((ext) => file.name.toLowerCase().endsWith(ext));
    if (!okType) {
      toast.error("Please upload a PDF, DOC or DOCX file.");
      e.target.value = "";
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      toast.error("CV is too large (max 6MB).");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCv({ name: file.name, base64: reader.result, mime: file.type });
    };
    reader.readAsDataURL(file);
  };

  const toggleAvailability = (opt) =>
    setForm((f) => ({
      ...f,
      availability: f.availability.includes(opt)
        ? f.availability.filter((a) => a !== opt)
        : [...f.availability, opt],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = {
      "First name": form.firstName, "Last name": form.lastName, "Email": form.email,
      "Phone": form.phone, "Location": form.location,
      "Previous experience": form.experience, "Driving licence": form.drivingLicence,
      "Right to work in the UK": form.rightToWork,
    };
    const missing = Object.entries(required).filter(([, v]) => !v || !String(v).trim()).map(([k]) => k);
    if (missing.length) {
      toast.error(`Please complete: ${missing.join(", ")}`);
      return;
    }
    if (form.availability.length === 0) {
      toast.error("Please select at least one availability option.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (cv) {
        payload.cvFileName = cv.name;
        payload.cvMimeType = cv.mime;
        payload.cvBase64 = cv.base64;
      }
      await axios.post(`${API}/apply`, payload);
      toast.success("Application sent! Thanks for applying, we\u2019ll be in touch soon.");
      setForm(initialForm);
      setCv(null);
    } catch (err) {
      toast.error("Sorry, something went wrong. Please call us on " + company.phone + ".");
    } finally {
      setLoading(false);
    }
  };

  const Req = () => <span className="text-[#ea731d]">*</span>;

  return (
    <div className="overflow-hidden">
      <Seo
        title="Cleaning Jobs in Manchester | iClean NW"
        description="Looking for cleaning jobs in Manchester and the North West? Join the iClean NW team. Apply online with your details, experience and availability. Flexible hours, supportive team and room to grow."
        path="/careers"
      />
      {/* Header */}
      <section className="pt-32 lg:pt-40 pb-4">
        <div className="max-w-6xl mx-auto px-5 lg:px-6">
          <span className="inline-flex items-center gap-2 text-[#ea731d] font-semibold tracking-[0.2em] text-xs uppercase">
            <Briefcase size={15} /> Careers
          </span>
          <h1 className="mt-3 text-4xl lg:text-6xl font-bold text-[#1a1714] leading-[1.05] max-w-3xl">
            Join the iClean team
          </h1>
          <p className="mt-5 text-lg text-neutral-600 max-w-2xl">
            We&rsquo;re always looking for reliable, hard-working people to join our growing team across {company.area}. Tell us about yourself below.
          </p>
          <p className="mt-4 text-neutral-600 max-w-2xl">
            At iClean NW we pride ourselves on high standards, a friendly team and looking after our staff. Every team member is fully vetted and trained in health and safety before starting, and we offer flexible hours to suit part-time, full-time and ad-hoc availability. Whether you have years of commercial cleaning experience or are just getting started, we&rsquo;d love to hear from you. Complete the short application form below and our team will be in touch to discuss the next steps.
          </p>
        </div>
      </section>

      {/* Perks */}
      <section className="py-10">
        <div className="max-w-6xl mx-auto px-5 lg:px-6 grid sm:grid-cols-3 gap-5">
          {perks.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.title} delay={i * 90}>
                <div className="bg-white rounded-[22px] p-6 h-full border border-neutral-100 hover:shadow-lg transition-shadow">
                  <div className="w-11 h-11 rounded-xl bg-[#ea731d]/12 text-[#ea731d] flex items-center justify-center"><Icon size={21} /></div>
                  <h3 className="mt-4 font-bold text-[#1a1714]">{p.title}</h3>
                  <p className="mt-1.5 text-sm text-neutral-600 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Form */}
      <section className="py-10 lg:py-16">
        <div className="max-w-4xl mx-auto px-5 lg:px-6">
          <Reveal>
            <form onSubmit={handleSubmit} className="bg-white rounded-[28px] p-7 lg:p-10 border border-neutral-100 soft-shadow">
              <h2 className="text-2xl font-bold text-[#1a1714]">Application form</h2>
              <p className="text-sm text-neutral-500 mt-1">Fields marked <Req /> are required.</p>

              {/* Personal details */}
              <h3 className="mt-8 mb-4 text-xs font-semibold uppercase tracking-widest text-[#ea731d]">Your details</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-sm font-medium">First name <Req /></Label>
                  <Input name="firstName" value={form.firstName} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="John" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Last name <Req /></Label>
                  <Input name="lastName" value={form.lastName} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="Smith" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Email <Req /></Label>
                  <Input name="email" type="email" value={form.email} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="you@example.com" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Phone <Req /></Label>
                  <Input name="phone" value={form.phone} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="07000 000000" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-sm font-medium">Town / Postcode <Req /></Label>
                  <Input name="location" value={form.location} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="e.g. Manchester, M1" />
                </div>
              </div>

              {/* Role & experience */}
              <h3 className="mt-8 mb-4 text-xs font-semibold uppercase tracking-widest text-[#ea731d]">Experience</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-sm font-medium">Years of experience</Label>
                  <Select value={form.experienceYears} onValueChange={(v) => setField("experienceYears", v)}>
                    <SelectTrigger className="mt-2 h-12 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {EXPERIENCE.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-sm font-medium">Previous experience <Req /></Label>
                  <Textarea name="experience" value={form.experience} onChange={handleChange} rows={4} className="mt-2 rounded-xl" placeholder="Tell us about any relevant cleaning or work experience..." />
                </div>
              </div>

              {/* Eligibility */}
              <h3 className="mt-8 mb-4 text-xs font-semibold uppercase tracking-widest text-[#ea731d]">Eligibility &amp; transport</h3>
              <div className="grid sm:grid-cols-3 gap-5">
                <div>
                  <Label className="text-sm font-medium">Driving licence <Req /></Label>
                  <Select value={form.drivingLicence} onValueChange={(v) => setField("drivingLicence", v)}>
                    <SelectTrigger className="mt-2 h-12 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{YES_NO.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Own transport</Label>
                  <Select value={form.ownTransport} onValueChange={(v) => setField("ownTransport", v)}>
                    <SelectTrigger className="mt-2 h-12 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{YES_NO.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Right to work in UK <Req /></Label>
                  <Select value={form.rightToWork} onValueChange={(v) => setField("rightToWork", v)}>
                    <SelectTrigger className="mt-2 h-12 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{YES_NO.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {/* Availability */}
              <h3 className="mt-8 mb-4 text-xs font-semibold uppercase tracking-widest text-[#ea731d]">Availability</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-sm font-medium">Employment type</Label>
                  <Select value={form.employmentType} onValueChange={(v) => setField("employmentType", v)}>
                    <SelectTrigger className="mt-2 h-12 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{EMPLOYMENT.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Available from</Label>
                  <Input name="startDate" value={form.startDate} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="e.g. Immediately, or a date" />
                </div>
              </div>
              <div className="mt-5">
                <Label className="text-sm font-medium">When can you work? <Req /></Label>
                <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                  {AVAILABILITY.map((opt) => (
                    <label key={opt} htmlFor={`av-${opt}`} className="flex items-center gap-3 cursor-pointer text-sm text-neutral-700">
                      <Checkbox id={`av-${opt}`} checked={form.availability.includes(opt)} onCheckedChange={() => toggleAvailability(opt)} />
                      {opt}
                    </label>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div className="mt-8">
                <Label className="text-sm font-medium">Anything else you&rsquo;d like us to know?</Label>
                <Textarea name="notes" value={form.notes} onChange={handleChange} rows={3} className="mt-2 rounded-xl" placeholder="Optional" />
              </div>

              {/* CV upload (optional) */}
              <div className="mt-6">
                <Label className="text-sm font-medium">Attach your CV <span className="text-neutral-400 font-normal">(optional)</span></Label>
                {!cv ? (
                  <label htmlFor="cv-upload" className="mt-2 flex items-center gap-3 cursor-pointer rounded-xl border border-dashed border-neutral-300 hover:border-[#ea731d] hover:bg-[#ea731d]/5 transition-colors px-5 py-4 text-neutral-600">
                    <span className="w-10 h-10 rounded-lg bg-[#ea731d]/12 text-[#ea731d] flex items-center justify-center flex-shrink-0"><Paperclip size={18} /></span>
                    <span className="text-sm"><span className="font-semibold text-[#1a1714]">Click to upload</span> &middot; PDF, DOC or DOCX (max 6MB)</span>
                    <input id="cv-upload" type="file" accept=".pdf,.doc,.docx" onChange={handleCv} className="hidden" />
                  </label>
                ) : (
                  <div className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-neutral-200 bg-[#f7f7f5] px-5 py-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-10 h-10 rounded-lg bg-[#ea731d]/12 text-[#ea731d] flex items-center justify-center flex-shrink-0"><Paperclip size={18} /></span>
                      <span className="text-sm font-medium text-[#1a1714] truncate">{cv.name}</span>
                    </div>
                    <button type="button" onClick={() => setCv(null)} className="flex-shrink-0 text-neutral-500 hover:text-[#ea731d] transition-colors" aria-label="Remove CV">
                      <X size={18} />
                    </button>
                  </div>
                )}
              </div>

              <button type="submit" disabled={loading} className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#ea731d] hover:bg-[#d9640f] disabled:opacity-70 text-white font-semibold px-8 py-3.5 rounded-full transition-all hover:-translate-y-0.5">
                {loading ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : <><Send size={17} /> Submit application</>}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Careers;
